import React from 'react';
import { X, Calendar, User, Award, TrendingUp, FileText, ClipboardList } from 'lucide-react';

interface AvaliacaoComDetalhes {
  id: string;
  alunoId: string;
  professorId: string;
  nivelId: string;
  proximoNivelId?: string;
  dataAvaliacao: string;
  notaGeral: number;
  aprovado: boolean;
  comentario?: string;
  mudancas?: string;
  createDate: string;
  updateDate?: string;
  deleteDate?: string;
  aluno?: {
    id: string;
    nome: string;
    email: string;
    fotoUrl?: string;
  } | null;
  professor?: {
    id: string;
    nome: string;
    email: string;
  } | null;
  nivel?: {
    id: string;
    nome: string;
  } | null;
  proximoNivel?: {
    id: string;
    nome: string;
  } | null;
}

interface AvaliacaoDetalhesModalProps {
  avaliacao: AvaliacaoComDetalhes;
  onClose: () => void;
}

const AvaliacaoDetalhesModal: React.FC<AvaliacaoDetalhesModalProps> = ({
  avaliacao,
  onClose
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Detalhes da Avaliação
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Porcentagem */}
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Porcentagem</span>
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {((avaliacao.notaGeral || 0) * 100).toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Informações da Avaliação */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              Informações da Avaliação
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Data da Avaliação</span>
                </div>
                <p className="text-sm text-gray-900 dark:text-white">
                  {new Date(avaliacao.dataAvaliacao).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Nível Atual</span>
                </div>
                <p className="text-sm text-gray-900 dark:text-white">
                  {avaliacao.nivel?.nome || 'Nível não encontrado'}
                </p>
              </div>

              {avaliacao.proximoNivel && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Próximo Nível</span>
                  </div>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {avaliacao.proximoNivel.nome}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Informações do Aluno */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              Aluno Avaliado
            </h3>

            <div className="flex items-center gap-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              {avaliacao.aluno?.fotoUrl ? (
                <img
                  src={avaliacao.aluno.fotoUrl}
                  alt={avaliacao.aluno.nome}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
              )}
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {avaliacao.aluno?.nome || 'Aluno não encontrado'}
                </p>
                {avaliacao.aluno?.email && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {avaliacao.aluno.email}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Informações do Professor */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              Professor Avaliador
            </h3>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {avaliacao.professor?.nome || 'Professor não encontrado'}
              </p>
              {avaliacao.professor?.email && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {avaliacao.professor.email}
                </p>
              )}
            </div>
          </div>

          {/* Comentários */}
          {avaliacao.comentario && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                Comentários
              </h3>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                    {avaliacao.comentario}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mudanças Sugeridas */}
          {avaliacao.mudancas && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                Mudanças Sugeridas
              </h3>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                    {avaliacao.mudancas}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Informações Adicionais */}
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              Informações Adicionais
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Data de Criação:</span>
                <p className="text-gray-900 dark:text-white">
                  {new Date(avaliacao.createDate).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              {avaliacao.updateDate && (
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Última Atualização:</span>
                  <p className="text-gray-900 dark:text-white">
                    {new Date(avaliacao.updateDate).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvaliacaoDetalhesModal;

